/**
 * TKP-102 — gönderim ekranının saf yardımcıları. Taslak → paket → düzenleme ağacından kullanıcının kendi paketlerini ve
 * aynı birimde başkalarının düzenlemelerini (md. 27: adıyla) çıkarır.
 */
/** Onay sekmesi yalnız KVKK/GDPR'da ve onay süreci AÇIKKEN görünür; kapalıyken Varlıklar bugünkü gibi sekmesizdir. */
export const approvalTabVisible = (product, status) => ["KVKK", "GDPR"].includes(product) && !!(status && status.enabled);

export const PACKAGE_STATUS = { OPEN: "OPEN", SUBMITTED: "SUBMITTED", COMPLETED: "COMPLETED", WITHDRAWN: "WITHDRAWN" };

const id = (v) => String((v && v._id) || v || "");

/** Kullanıcının paketleri (açık ya da onayda bekleyen), taslağın birimiyle birlikte. */
export const myPackages = (drafts, accountId) =>
  // TKP-237 m.2: tamamlanan taslak tarihçede; üst bölümde yalnız açık / gönderilmiş
  (drafts || []).flatMap((draft) => (draft.packages || []).filter((p) => id(p.author) === id(accountId) && p.status !== PACKAGE_STATUS.WITHDRAWN && p.status !== PACKAGE_STATUS.COMPLETED).map((p) => ({ ...p, department: draft.department, draftMode: draft.mode }))); // TKP-134: birimin etkin onay modu

/** Aynı birimde başka yazarların düzenlemeleri — gönderim ekranında bilgi olarak, yazar adıyla (md. 24, 27). */
export const othersEdits = (drafts, department, accountId) =>
  (drafts || [])
    .filter((draft) => id(draft.department) === id(department))
    .flatMap((draft) => (draft.packages || []).filter((p) => id(p.author) !== id(accountId) && p.status !== PACKAGE_STATUS.WITHDRAWN).flatMap((p) => (p.edits || []).map((e) => ({ ...e, authorName: e.authorName || p.authorName, packageStatus: p.status }))));

/** TKP-155 / tunesoft2 s.4: taslak başlığı — sistemin verdiği ad ("#BirimAdı_GGAAYY#"); eski pakette (ad yok) birim adı. */
export const packageTitle = (pkg, departmentName) => (pkg && pkg.name) || departmentName || "";

/** TKP-200 m.14: isteğin kendi alanları (kapsam vb.) kayıt değeri değildir — onay ekranında hiç gösterilmez (eski taslaklarda da). */
export const HIDDEN_EDIT_FIELDS = Object.freeze(["scope", "header", "pagination", "organization", "createdAt", "updatedAt", "__v"]);
const visibleFields = (edit) => (edit && edit.changedFields ? edit.changedFields : []).filter((f) => !HIDDEN_EDIT_FIELDS.includes(f));

/**
 * TKP-155: satırın "Filtre" sütunu — kayıt bağlamı (birim › süreç › veri › kayıt) ve değişen alanlar. TKP-200 m.14: yeni kayıtta
 * (oluşturma) bütün alanlar "değişmiş" sayıldığından alan listesi anlamsız ("Veri düğümü, Kütüphane kaydı") — bağlam yeter.
 */
export const filterLine = (edit, language = "tr", fieldLabel = (f) => f) => {
  const context = contextLine(edit && edit.context, language);
  if (context && edit && edit.operation === "CREATE") return context;
  const fields = visibleFields(edit).map((f) => fieldLabel(f)).join(", ");
  return [context, fields].filter(Boolean).join(" › ");
};

/** Alan adının yerelleştirme anahtarı; karşılığı yoksa bileşen ham alan adını gösterir. */
export const fieldLabelKey = (field) => `approval.field.${field}`;

const localized = (n) => (n && typeof n === "object" ? n.tr || n.en || n.de || n.fr : n) || "";

/** TKP-241 p.10: listede adı çözülemeyen öğe ("—") diğerlerinin arasına yazılmaz. */
const joinLabels = (labels) => {
  const shown = labels.filter((v) => v && v !== "—");
  return shown.length ? shown.join(", ") : "—";
};
const COMPOSITE_SKIP = ["_id", "__v", "createdAt", "updatedAt", "organization", "createdBy", "datasubject", "processData", "process", "department", "data", "datasource"];
const COMPOSITE_ORDER = ["purpose", "legalReason"]; // TKP-241 p.10: "İşleme amacı / Hukuki Sebep"
const compositeRank = (key) => (COMPOSITE_ORDER.includes(key) ? COMPOSITE_ORDER.indexOf(key) : COMPOSITE_ORDER.length);

/** TKP-131: başvurunun görünen adı — sunucunun ad haritası, değilse gömülü ad, değilse kimlik. */
export const refLabel = (value, names = {}) => {
  if (value === null || value === undefined) return "—";
  if (Array.isArray(value)) return joinLabels(value.map((v) => refLabel(v, names))); // TKP-240 b.10
  if (typeof value !== "object") return localized(names[String(value)]) || (/^[0-9a-f]{24}$/i.test(String(value)) ? "—" : String(value));
  const own = localized(names[id(value._id || "")]) || localized(value.name) || (value.tr || value.en ? localized(value) : "");
  if (own) return own;
  const named = value._id ? localized(names[id(value)]) : "";
  if (named) return named;
  // TKP-240 b.10: adı / kimliği olmayan bileşik değer (ör. amaç + hukuki sebep, ülke + alıcı grubu) — "[object Object]" yerine parçaları
  // TKP-241 p.10: kaydın bağlı olduğu düğüm alanları (ilgili kişi, veri…) parça değil — "… / 6ae9…" kimliği yazıyordu; amaç önce
  const parts = Object.entries(value)
    .filter(([k]) => !COMPOSITE_SKIP.includes(k))
    .sort(([a], [b]) => compositeRank(a) - compositeRank(b))
    .map(([, v]) => (v && typeof v === "object" && v.libraryItem ? refLabel(v.libraryItem, names) : refLabel(v, names)))
    .filter((v) => v && v !== "—" && !/^[0-9a-f]{24}$/i.test(v));
  if (parts.length) return parts.join(" / ");
  // TKP-241 p.10: adı çözülemeyen kayıt kimliği ham gösterilmez (kayıtlı işleme kalemi, `{_id}` biçimli bağ)
  return value._id && !/^[0-9a-f]{24}$/i.test(id(value)) ? id(value) : "—";
};

/** Düzenleme satırı özeti: tür, işlem, değişen alanlar, eski→yeni (yalnız değişen alanlar, kısa gösterim). */
export const summarize = (edit) => {
  const fields = visibleFields(edit);
  const names = edit.names || {};
  const show = (value) => {
    if (value === null || value === undefined) return "—";
    // TKP-131: kaynak listesi gibi başvuru dizileri ADLA (sunucu `names` haritası), ham `[{"_id":…}]` değil
    if (Array.isArray(value)) return joinLabels(value.map((v) => refLabel(v, names)));
    if (typeof value === "object") return value.tr || value.en || (value._id ? refLabel(value, names) : value.name ? localized(value.name) : JSON.stringify(value)); // TKP-200 m.14: kütüphane kalemi adıyla
    const name = localized(names[String(value)]);
    if (name) return name; // kayıtlı hâlde başvuru düz kimlik olarak durur
    // TKP-200 m.14: adı çözülemeyen kimlik (24 haneli) ham gösterilmez
    return /^[0-9a-f]{24}$/i.test(String(value)) ? "—" : String(value);
  };
  return fields.map((field) => ({ field, before: edit.before ? show(edit.before[field]) : "—", after: edit.after ? show(edit.after[field]) : "—" }));
};

export const canSubmit = (pkg) => pkg.status === PACKAGE_STATUS.OPEN && (pkg.edits || []).length > 0;
/** TKP-158: düzeltilmemiş düzenleme isteği taşıyan satır sayısı — gönderim bunlar düzeltilmeden ya da kaldırılmadan yapılmaz. */
export const openEditRequests = (pkg) => (pkg && pkg.edits ? pkg.edits : []).filter((e) => (!e.status || e.status === "PENDING") && e.editRequest && e.editRequest.reason).length;

// --- TKP-134: onay modu ve kademe (matris D, karar 2026-09-28)
export const APPROVAL_MODE = { STANDARD: "STANDARD", PRE_APPROVAL: "PRE_APPROVAL", NOTIFY: "NOTIFY" };
export const PACKAGE_STAGE = { DEPARTMENT: "DEPARTMENT", LEGAL: "LEGAL" };
export const modeLabelKey = (mode) => `approval.mode.${mode || APPROVAL_MODE.STANDARD}`;
export const stageLabelKey = (stage) => `approval.stage.${stage || PACKAGE_STAGE.LEGAL}`;
/** Gönderim penceresi: bildirim modunda onaylayan seçilmez ("Yayınla"); ön onayda birim onaylayanı da seçilir (aday yoksa kademe atlanır). */
export const submitDialogFrom = (packageId, data) => ({
  show: true,
  packageId,
  mode: (data && data.mode) || APPROVAL_MODE.STANDARD,
  modeSource: (data && data.modeSource) || "ORGANIZATION", // TKP-237 m.3: birime özel mod mu, organizasyon modu mu
  approver: data && data.suggested ? data.suggested.account : null,
  candidates: (data && data.candidates) || [],
  departmentApprover: data && data.departmentSuggested ? data.departmentSuggested.account : null,
  departmentCandidates: (data && data.departmentCandidates) || [],
});
export const submitReady = (dialog) => (dialog.mode === APPROVAL_MODE.NOTIFY ? true : !!dialog.approver);
export const submitPayload = (dialog) => ({ packageId: dialog.packageId, approver: dialog.mode === APPROVAL_MODE.NOTIFY ? undefined : dialog.approver, departmentApprover: dialog.mode === APPROVAL_MODE.PRE_APPROVAL && dialog.departmentApprover ? dialog.departmentApprover : undefined });
/** TKP-138: başvuru / veri ihlali taslakları modül başına sabit kapsamda; kapsam adı çeviri anahtarı ya da null (birim). */
export const FIXED_SCOPE_KEYS = { __APPLICATION__: "approval.scope.APPLICATION", __DATA_BREACH__: "approval.scope.DATA_BREACH" };
export const fixedScopeKey = (department) => FIXED_SCOPE_KEYS[String(department)] || null;

/** Paket kartı için kademe rozeti: yalnız onayda bekleyen pakette; eski kayıtta kademe yok = Uyum. */
export const stageOf = (pkg) => (pkg && pkg.status === PACKAGE_STATUS.SUBMITTED ? pkg.stage || PACKAGE_STAGE.LEGAL : null);
export const canWithdraw = (pkg) => pkg.status === PACKAGE_STATUS.SUBMITTED;

// --- TKP-103: onaylayan tarafı
export const DECISION = { APPROVE: "APPROVE", EDIT: "EDIT", REJECT: "REJECT" };

/** Bağlam satırı: birim › süreç › veri › ilgili kişi › kayıt (olanlar, verilen dilde). */
export const contextLine = (context, language = "tr") =>
  ["department", "process", "datasource", "data", "datasubject", "record"]
    .map((k) => context && context[k])
    .filter(Boolean)
    .map((name) => (typeof name === "string" ? name : name[language] || name.tr || name.en || ""))
    .filter(Boolean)
    .join(" › ");

/** TKP-157 / tunesoft2 s.7 — satır durumu saf kuralları. Bekleyen satır: APPLIED / REJECTED / FAILED değil. */
export const isPendingRow = (edit) => !edit || !edit.status || edit.status === "PENDING";
/** "Geri Gönder": en az bir bekleyen satırda düzenleme isteği varsa. */
export const canReturnPackage = (pkg) => (pkg && pkg.edits ? pkg.edits : []).some((e) => isPendingRow(e) && !!(e.editRequest && e.editRequest.reason)); // şema minimize:false → boş nesne gelebilir
/** "Tamamlandı": düzenleme istenen satır yok ve karar verilmemiş bekleyen satır yok (birim kademesinde ön kararı olanlar sayılır). */
export const canCompletePackage = (pkg) => {
  const edits = pkg && pkg.edits ? pkg.edits : [];
  if (edits.some((e) => isPendingRow(e) && e.editRequest && e.editRequest.reason)) return false;
  const stage = (pkg && pkg.stage) || "LEGAL";
  return !edits.some((e) => isPendingRow(e) && !(stage === "DEPARTMENT" && e.preDecision && e.preDecision.kind));
};

/** Karar formu başlangıcı: her düzenleme APPROVE; EDIT için değerler düzenlemenin yeni değerinden (yalnız değişen alanlar) başlar. */
export const initialDecisions = (pkg) =>
  Object.fromEntries((pkg.edits || []).map((e) => [String(e._id), { decision: DECISION.APPROVE, reason: "", values: editableValues(e) }]));

/** Onaylayanın düzenleyebileceği alanlar: değişen alanların yeni değeri (JSON metni olarak; ilkel değerler düz). */
export const editableValues = (edit) => Object.fromEntries((edit.changedFields || []).filter((f) => edit.after && f in edit.after).map((f) => [f, edit.after[f]]));

/** Sunucuya gidecek karar listesi; EDIT'te yalnız gerçekten değişen değerler gönderilir. */
export const toDecisionPayload = (pkg, form) =>
  (pkg.edits || []).map((e) => {
    const d = form[String(e._id)] || { decision: DECISION.APPROVE };
    const base = { edit: String(e._id), decision: d.decision };
    if (d.decision === DECISION.REJECT) return { ...base, reason: d.reason || "" };
    if (d.decision === DECISION.EDIT) {
      const original = editableValues(e);
      const values = Object.fromEntries(Object.entries(d.values || {}).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(original[k])));
      return { ...base, values, reason: d.reason || undefined };
    }
    return base;
  });

/** Gönderilebilir mi: her REJECT'in gerekçesi olmalı. */
export const decisionsValid = (pkg, form) => (pkg.edits || []).every((e) => { const d = form[String(e._id)]; return !d || d.decision !== DECISION.REJECT || (d.reason || "").trim().length > 0; });

/** Değer alanı: ilkel/yerelleştirilmiş metin düz düzenlenir; nesne/dizi JSON metni olarak. */
export const isPlainValue = (v) => v === null || ["string", "number", "boolean"].includes(typeof v);
export const isLocalized = (v) => !!v && typeof v === "object" && !Array.isArray(v) && Object.keys(v).every((k) => ["tr", "en", "de", "fr"].includes(k));

// --- TKP-104: taslakta çalışma — kayıt başına taslak / kilit durumu (md. 13, 15)
const ACTIVE = [PACKAGE_STATUS.OPEN, PACKAGE_STATUS.SUBMITTED];

/**
 * Kaydın taslak durumu: { mine, edit, packageStatus, authorName } ya da null.
 * `mine`: kullanıcının kendi (açık ya da onayda bekleyen) düzenlemesi; değilse başkasının kilidi (md. 15).
 */
export const draftStateOf = (drafts, entityType, entityId, accountId) => {
  for (const draft of drafts || []) {
    for (const p of draft.packages || []) {
      if (!ACTIVE.includes(p.status)) continue;
      const edit = (p.edits || []).find((e) => e.entityType === entityType && String(e.entityId) === String(entityId) && (!e.status || e.status === "PENDING"));
      if (edit) return { mine: id(p.author) === id(accountId), edit, packageStatus: p.status, authorName: edit.authorName || p.authorName || "" };
    }
  }
  return null;
};

/**
 * TKP-251: birim / süreç kutusu — altında (süreç, veri, ilgili kişi, özellik) bekleyen taslak satırı var mı. Birim: taslağın birimi;
 * süreç: satırın bağlamındaki süreç kimliği (sunucu `context.processId`). Dönen: `{ mine, count }` ya da null. Saf.
 */
export const draftUnderOf = (drafts, entityType, entityId, accountId) => {
  let mine = 0;
  let others = 0;
  for (const draft of drafts || []) {
    if (entityType === "DEPARTMENT" && id(draft.department) !== id(entityId)) continue;
    for (const p of draft.packages || []) {
      if (!ACTIVE.includes(p.status)) continue;
      const rows = (p.edits || []).filter((e) => (!e.status || e.status === "PENDING") && (entityType === "DEPARTMENT" || (e.context && id(e.context.processId) === id(entityId))));
      if (id(p.author) === id(accountId)) mine += rows.length;
      else others += rows.length;
    }
  }
  return mine + others ? { mine: mine > 0, count: mine + others } : null;
};

/**
 * TKP-241 p.6: "Tamamlandı" işareti taslağa yazılmaz — birim / süreç taslaktaysa ("Taslakta" rozeti ya da taslakta açılmış kayıt)
 * işaretlenemez (sunucu da reddeder), değilse doğrudan işaretlenir.
 */
export const completeBlocked = (drafts, entityType, item, accountId) => !!(item && (item.__draft || draftStateOf(drafts, entityType, item._id, accountId)));

/** Kayıt başkasının taslağında mı (okunur, düzenlenemez). */
export const lockedByOther = (drafts, entityType, entityId, accountId) => {
  const state = draftStateOf(drafts, entityType, entityId, accountId);
  return state && !state.mine ? state : null;
};

/** Sunucunun taslak yanıtını ({draft:true, edit}) bugünkü akışların beklediği kayıt biçimine çevirir. */
export const entityFromDraftResponse = (data) => {
  if (!data || data.draft !== true || !data.edit) return data;
  const edit = data.edit;
  const base = edit.operation === "DELETE" ? {} : { ...(edit.after || {}) };
  return { ...base, _id: edit.entityId, __draft: { operation: edit.operation, shadow: !!edit.shadow, package: data.package, edit: edit._id } };
};

/** md. 15 mesajı mı (sunucu 403 gövdesi düz metin). */
export const isLockMessage = (text) => typeof text === "string" && /taslağında düzenleniyor|onayda bekleyen bir pakette|another user's draft|awaiting approval|Entwurf eines anderen|brouillon d'un autre/i.test(text);

// --- TKP-105: periyodik güncelleme takibi (md. 54–55)
/** Birim satırı kapalı mı (güncelleme yok ya da güncellendi). */
export const periodEntryClosed = (entry) => !!entry && (entry.status === "NO_CHANGE" || entry.status === "UPDATED");

/** Kullanıcı bu birim için "güncelleme yok" diyebilir mi: dönem açık, satır açık/onayda, kullanıcı onay yetkilisi ya da birimin yetkilisi. */
export const canDeclareNoChange = (period, entry, { approver, adminDepartmentIds }) =>
  !!period && period.status === "OPEN" && !!entry && !periodEntryClosed(entry) && (approver || (adminDepartmentIds || []).map(String).includes(String(entry.department)));

/** TKP-100: hatırlatma — "güncelleme yok" ile aynı kişiler ve aynı açık/onayda koşulu. */
export const canRemind = canDeclareNoChange;
/** TKP-160 / tunesoft2 s.9: "Güncelleme Tamamlandı" — "Güncelleme Yok" ile aynı kişiler ve koşul. */
export const canDeclareUpdated = canDeclareNoChange;
/** TKP-160: birim satırının ekran durumu — Devam Ediyor / Tamamlandı. */
/** TKP-181 md.102: "Sorumlu" hücresi — birimin/sürecin bütün yetkilileri (`assigneeNames`); eski yanıtta tek `assigneeName`; yoksa "—". */
export const responsibleNames = (row) => {
  const names = [...new Set(((row && row.assigneeNames) || []).filter(Boolean))];
  return names.length ? names.join(", ") : (row && row.assigneeName) || "—";
};
export const periodEntryStateKey = (entry) => (periodEntryClosed(entry) ? "approval.period.entry-state.completed" : "approval.period.entry-state.in-progress");
/** TKP-160: koşum "Tamamlandı" tuşu — onay yetkilisi, koşum açık, tüm birimler işaretli. */
export const canCompleteRun = (period, approver) => !!approver && !!period && period.status === "OPEN" && periodSummary(period).total > 0 && periodSummary(period).open === 0;

/** TKP-135: süreç satırı için "güncelleme yok" — dönem açık, birim ve süreç satırı açık/onayda; onay yetkilisi, birimin yetkilisi ya da sürecin yetkilisi. */
export const canDeclareProcessNoChange = (period, entry, proc, { approver, adminDepartmentIds, adminProcessIds }) =>
  !!period && period.status === "OPEN" && !!entry && !periodEntryClosed(entry) && !!proc && !periodEntryClosed(proc) &&
  (approver || (adminDepartmentIds || []).map(String).includes(String(entry.department)) || (adminProcessIds || []).map(String).includes(String(proc.process)));

/** TKP-179 md.63: süreç satırı "güncelleme tamamlandı" — "güncelleme yok" ile aynı kişiler ve koşul. */
export const canDeclareProcessUpdated = canDeclareProcessNoChange;
/** TKP-179 md.63: geri alma — koşum açık, satır kapalı; birim: onay yetkilisi ya da birimin yetkilisi. */
export const canReopenEntry = (period, entry, { approver, adminDepartmentIds }) =>
  !!period && period.status === "OPEN" && periodEntryClosed(entry) && (approver || (adminDepartmentIds || []).map(String).includes(String(entry.department)));
/** TKP-179 md.63: süreç satırı geri alma — koşum açık, süreç satırı kapalı; onay yetkilisi, birimin ya da sürecin yetkilisi. */
export const canReopenProcess = (period, entry, proc, { approver, adminDepartmentIds, adminProcessIds }) =>
  !!period && period.status === "OPEN" && !!entry && !!proc && periodEntryClosed(proc) &&
  (approver || (adminDepartmentIds || []).map(String).includes(String(entry.department)) || (adminProcessIds || []).map(String).includes(String(proc.process)));
/** TKP-200 m.6: koşum sürerken bu birimde/süreçte envanter değişikliği uygulandı (backend damgası) — "Güncelleme Yok" denemez. */
export const changedDuringRun = (row) => !!(row && row.changedAt);
/**
 * TKP-209: süreç satırının gösterilen durumu — kapalı birimde açık kalmış süreç satırı (eski koşumlar: birim beyanı süreçleri
 * kapatmadan önce) birimin durumunu alır; "Bekliyor" yalnız açık birimde görünür.
 */
export const processStatusOf = (entry, proc) => (proc && !periodEntryClosed(proc) && periodEntryClosed(entry) ? entry.status : proc && proc.status);
/** TKP-179 md.63: birimin süreç dağılımı: güncellendi / güncelleme yok / bekliyor (TKP-209: gösterilen durumla sayılır). */
export const processDistribution = (entry) => {
  const list = ((entry && entry.processes) || []).map((p) => processStatusOf(entry, p));
  const updated = list.filter((s) => s === "UPDATED").length;
  const noChange = list.filter((s) => s === "NO_CHANGE").length;
  return { total: list.length, updated, noChange, open: list.length - updated - noChange };
};
/** TKP-179 md.63: birim tuşları "kalan süreçler için" etiketini taşır — en az bir süreç işaretli ve en az biri bekliyorsa. */
export const hasMarkedProcesses = (entry) => {
  const d = processDistribution(entry);
  return d.total > 0 && d.updated + d.noChange > 0 && d.open > 0;
};

/** Özet: açık / kapalı birim sayısı. */
export const periodSummary = (period) => {
  const entries = (period && period.departments) || [];
  const closed = entries.filter(periodEntryClosed).length;
  return { total: entries.length, closed, open: entries.length - closed };
};

/** TKP-186 md.16: kendi açık/onaydaki paketlerimdeki bekleyen düzenlemeler ({ edit, packageStatus }) — gölge/taslak katmanı buradan okur. */
const pendingMineOf = (drafts, accountId) =>
  (drafts || []).flatMap((draft) => (draft.packages || []).filter((p) => ACTIVE.includes(p.status) && id(p.author) === id(accountId)).flatMap((p) => (p.edits || []).filter((e) => !e.status || e.status === "PENDING").map((e) => ({ edit: e, packageStatus: p.status }))));
/**
 * TKP-254: katman yardımcıları satır / veri başına çağrılır (kalabalık birimde binlerce kez) — sonuç aynı taslak dizisi ve kullanıcı için
 * bir kez hesaplanır. Taslak listesi store'da hep bütünüyle değiştirilir (yerinde değişmez), dizi kimliği önbellek anahtarıdır.
 */
const pendingCache = new WeakMap();
export const pendingMine = (drafts, accountId) => {
  if (!drafts || typeof drafts !== "object") return [];
  let byAccount = pendingCache.get(drafts);
  if (!byAccount) pendingCache.set(drafts, (byAccount = new Map()));
  const key = id(accountId);
  if (!byAccount.has(key)) byAccount.set(key, Object.freeze(pendingMineOf(drafts, accountId)));
  return byAccount.get(key);
};

/**
 * TKP-131 — sürece kendi taslağımda eklediğim (henüz envanterde olmayan) veri düğümleri. Süreç yöneticisi eklediği verinin
 * taslağa düştüğünü kutularda görür; ad organizasyon verisinden ya da kütüphane kaleminden, kaynaklar adıyla.
 */
export const shadowNodesOf = (drafts, processId, accountId) =>
  pendingMine(drafts, accountId)
    .filter(({ edit }) => edit.entityType === "NODE" && edit.operation === "CREATE" && edit.after && id(edit.after.process) === id(processId))
    .map(({ edit, packageStatus }) => {
      const names = edit.names || {};
      const after = edit.after;
      const name = names[id(after.data)] || (after.data && after.data.name) || (after.libraryItem && after.libraryItem.name) || null;
      return {
        _id: edit.entityId,
        name,
        // TKP-189: seçilebilir gölge düğüm — alt kutular ve başlıklar gerçek düğüm gibi okur (`data.name`, `process._id`)
        data: { _id: id(after.data) || undefined, name },
        process: { _id: id(processId) },
        datasources: (after.datasources || []).map((d) => ({ _id: id(d), name: names[id(d)] || (d && d.name) || null })),
        packageStatus,
        __draft: { shadow: true, packageStatus, edit: edit._id },
      };
    });

/** TKP-131 — düğümün kaynakları kendi taslağımda değiştiyse taslaktaki liste (adıyla); değişmediyse null. */
export const draftDatasourcesOf = (drafts, nodeId, accountId) => {
  const state = draftStateOf(drafts, "NODE", nodeId, accountId);
  if (!state || !state.mine || !state.edit.after || !(state.edit.changedFields || []).includes("datasources")) return null;
  const names = state.edit.names || {};
  return (state.edit.after.datasources || []).map((d) => ({ _id: id(d), name: names[id(d)] || (d && d.name) || null }));
};

